import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';

const messages = defineMessages({
	action: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.flag-field.comment.non-final',
		defaultMessage: 'Add comment',
		description: 'Label for the change action in the comment field',
	},
});

export type Props = {
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const CommentField = ({
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const isFieldRenderable = action?.data?.fieldKey === 'comment';

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				suggested={
					action?.data?.fieldValue !== undefined && action?.data?.fieldValue !== null
						? String(action.data.fieldValue)
						: undefined
				}
			/>
		),
		[action?.data?.fieldValue, action?.data?.reasoning],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable) {
		return null;
	}

	const comment = String(action.data.fieldValue)?.trim();
	if (!comment) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.comment-field"
		>
			{formatMessage(messages.action)}
		</Text>
	);
};
